<template>

<div class="af3-shotcoder-filter-bar-slider-container" ref="componentRef">
    <button class="af3-shotcoder-filter-bar-slider-toggle-button" :aria-expanded="is_open" @click="is_open = !is_open">
        
        <div>
        <template v-if="typeof handleLabelString === 'string'">
            {{ handleLabelString }}
        </template>
        <template v-else>
            <template v-if="is_price">
                <span v-dollar-format="handleLabelString.min"></span> - <span v-dollar-format="handleLabelString.max"></span>
            </template>
            <template v-else>
                {{ handleLabelString.min }} - {{ handleLabelString.max }}
            </template>
        </template>
        </div>

        <svg xmlns="http://www.w3.org/2000/svg" width="11.304" height="6.712" viewBox="0 0 11.304 6.712">
            <path id="Path_46144" data-name="Path 46144" d="M9.713,5.652,5.122,1.061.53,5.652-.53,4.591,5.122-1.061l5.652,5.652Z" transform="translate(10.773 5.652) rotate(180)" />
        </svg>

    </button>
    
    <div class="af3-shotcoder-filter-bar-slider-range-number-container-main-wrapper" :aria-hidden="!is_open">

        <div class="af3-shotcoder-filter-bar-slider-range-number-container-wrapper">
            <!-- range number -->
            <div class="af3-shotcoder-filter-bar-slider-range-number-container">
                <div class="af3-shotcoder-filter-bar-slider-range-number-container-item left">
                    <span class="af3-shotcoder-filter-bar-slider-range-number-container-item-label">Minimum</span>
                    <span v-if="is_price" v-dollar-format="selected_min"></span>
                    <span v-else>{{ selected_min }}</span>
                </div>
                <div class="af3-shotcoder-filter-bar-slider-range-number-container-item right">
                    <span class="af3-shotcoder-filter-bar-slider-range-number-container-item-label">Maximum</span>
                    <span v-if="is_price" v-dollar-format="selected_max"></span>
                    <span v-else>{{ selected_max }}</span>
                </div>
            </div>
            <!-- slider -->
            <div class="af3-shotcoder-filter-bar-slider-range-number-slider" ref="sliderTrack">
                <button 
                    class="af3-shotcoder-filter-bar-slider-range-number-slider-button min" 
                    ref="minButton"
                    :style="{ left: `${minPosition}%` }"
                    @mousedown="startDrag($event, 'min')"
                    @touchstart="startDrag($event, 'min')"
                ></button>
                <button 
                    class="af3-shotcoder-filter-bar-slider-range-number-slider-button" 
                    ref="maxButton"
                    :style="{ left: `${maxPosition}%` }"
                    @mousedown="startDrag($event, 'max')"
                    @touchstart="startDrag($event, 'max')"
                ></button>
            </div>
        </div>
    </div>
</div>
</template>
  
<script>

import { defineComponent, ref, watch, onMounted, reactive, computed, nextTick, onUnmounted } from 'vue';


export default defineComponent({
    components: { },
   
    props: { 
        min: {
            type: Number,
            required: true
        },
        max: {
            type: Number,
            required: true
        },
        valueMin: {
            type: Number,
            required: false,
            default: null
        },
        valueMax: {
            type: Number,
            required: false,
            default: null
        },
        is_price: {
            type: Boolean,
            required: true
        }
    },

    emits: ['update:min', 'update:max'],

    setup(props, { emit }) {

        const is_open = ref(false);
        const componentRef = ref(null);  // Add ref for the component root element
        
        // Initialize with valueMin/valueMax if provided, otherwise use min/max
        const selected_min = ref(props.valueMin !== null ? Number(props.valueMin) : Number(props.min) || 0);
        const selected_max = ref(props.valueMax !== null ? Number(props.valueMax) : Number(props.max) || 0);
        
        const sliderTrack = ref(null);
        const minButton = ref(null);
        const maxButton = ref(null);
        const isDragging = ref(false);
        const currentHandle = ref(null);
        
        // Computed position for each handle based on current values
        const minPosition = computed(() => {
            const range = props.max - props.min;
            if (range === 0) return 0;
            return ((selected_min.value - props.min) / range) * 100;
        });
        
        const maxPosition = computed(() => {
            const range = props.max - props.min;
            if (range === 0) return 100;
            return ((selected_max.value - props.min) / range) * 100;
        });
        
        // Watch for changes in value props
        watch(() => props.valueMin, (newVal) => {
            if (newVal !== null) {
                selected_min.value = Number(newVal);
            } else {
                selected_min.value = Number(props.min); // Default to min if valueMin is null
            }
        });
        
        watch(() => props.valueMax, (newVal) => {
            if (newVal !== null) {
                selected_max.value = Number(newVal);
            } else {
                selected_max.value = Number(props.max); // Default to max if valueMax is null
            }
        });
        
        // Watch for changes in min/max range props
        watch(() => props.min, (newVal) => {
            if (props.valueMin === null) {
                selected_min.value = Number(newVal) || 0;
            }
        });
        
        watch(() => props.max, (newVal) => {
            if (props.valueMax === null) {
                selected_max.value = Number(newVal) || 0;
            }
        });

        // Watch for changes in selected values to emit updates
        watch(selected_min, (newVal) => {
            emit('update:min', newVal);
        });
        
        watch(selected_max, (newVal) => {
            emit('update:max', newVal);
        });
        
        // Drag handling
        const startDrag = (event, handle) => {
            event.preventDefault();
            isDragging.value = true;
            currentHandle.value = handle;
            
            // Add event listeners for drag and release
            document.addEventListener('mousemove', handleDrag);
            document.addEventListener('mouseup', stopDrag);
            document.addEventListener('touchmove', handleDrag);
            document.addEventListener('touchend', stopDrag);
        };
        
        const handleDrag = (event) => {
            if (!isDragging.value || !sliderTrack.value) return;
            
            const trackRect = sliderTrack.value.getBoundingClientRect();
            const trackWidth = trackRect.width;
            
            // Get clientX for both mouse and touch events
            const clientX = event.touches ? event.touches[0].clientX : event.clientX;
            
            // Calculate new position as percentage of track width
            let position = (clientX - trackRect.left) / trackWidth;
            position = Math.max(0, Math.min(1, position)); // Clamp between 0 and 1
            
            // Calculate new value based on position and range
            const range = props.max - props.min;
            const newValue = Math.round(props.min + position * range);
            
            if (currentHandle.value === 'min') {
                // Ensure min doesn't exceed max
                selected_min.value = Math.min(newValue, selected_max.value);
            } else {
                // Ensure max doesn't go below min
                selected_max.value = Math.max(newValue, selected_min.value);
            }
        };

        const handleLabelString = computed(() => {
            // If selected values are at the min and max range, return "Any"
            if (selected_min.value === props.min && selected_max.value === props.max) {
                return "Any";
            }
            
            // Otherwise, return the range values to be formatted in the template
            return {
                min: selected_min.value,
                max: selected_max.value,
                isRange: true
            };
        });
        
        const stopDrag = () => {
            isDragging.value = false;
            currentHandle.value = null;
            
            // Remove event listeners
            document.removeEventListener('mousemove', handleDrag);
            document.removeEventListener('mouseup', stopDrag);
            document.removeEventListener('touchmove', handleDrag);
            document.removeEventListener('touchend', stopDrag);
        };
        
        // Add click outside handler
        const handleClickOutside = (event) => {
            if (is_open.value && componentRef.value && !componentRef.value.contains(event.target)) {
                is_open.value = false;
            }
        };

        // Add and remove click outside listener
        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
            
            // Ensure initial values are valid
            if (selected_min.value > selected_max.value) {
                selected_min.value = selected_max.value;
            }
            
            // Force a refresh of the values to ensure they're properly initialized
            if (props.valueMin === null) {
                selected_min.value = Number(props.min);
            }
            
            if (props.valueMax === null) {
                selected_max.value = Number(props.max);
            }
        });

        // Clean up event listener
        onUnmounted(() => {
            document.removeEventListener('click', handleClickOutside);
        });

        return {
            selected_min,
            selected_max,
            sliderTrack,
            minButton,
            maxButton,
            minPosition,
            maxPosition,
            startDrag,
            handleLabelString,
            is_open,
            componentRef  // Add componentRef to the returned object
        };
    },
});
</script>